import _ from 'lodash';
import appConfig from 'app-config';
import queryString from 'query-string';
import { ServiceManager } from '@jutro/legacy/services';
import { MLBundleService } from 'ml-services-common-react';
import { CampaignDataService, MLProductCodeUtil } from 'ml-portals-util-js';

import qbConfig from './config/MLTagManagerConfig-QB';
import peConfig from './config/MLTagManagerConfig-PE';
import ceConfig from './config/MLTagManagerConfig-CE';

let auth;
let customerID = '';
let agentType = '';
let initialized = false;
let lastPageName = '';
let currentPageName = '';
let model;
const BUNDLED = 1;
const {
    portalCode
} = appConfig;

let mlTagManagerConfig;
if(portalCode === 'GPA'){
    mlTagManagerConfig = peConfig;
}else if(portalCode === 'AMP'){
    mlTagManagerConfig = ceConfig;
}else{
    mlTagManagerConfig = qbConfig;
}

const isAgent = portalCode === 'GPA';
const campaignData = CampaignDataService();

/**
 * Retrieves a URL parameter
 * If the parameter doesn't exist retrieves an empty string
 * @param {String} name name of the param to retrieve
 * @returns {String} value
 */
const _getUrlParameter = (name) => {
    if (Object.keys(campaignData).length === 0) {
        const hrefML = window.location.href;
        const queryML = hrefML.includes('?') ? hrefML.substr(hrefML.indexOf('?')) : '';
        const searchParams = queryString.parse(window.location.search || queryML);
        return (searchParams[name] !== undefined) ? searchParams[name] : '';
    }
    return (campaignData[name] !== undefined) ? campaignData[name] : '';
};

const getPathName = () => {
    return window.location.pathname
        .replace("/producer-engage/", "")
        .replace("/quote-and-buy/", "")
        .replace("/account-management/", "");
}

const _getQueryStringParameters = () => {
    if (campaignData.queryStringParameters) {
        return campaignData.queryStringParameters;
    }
    return window.location.search;
};

const _getLastPageData = () => {
    if ((currentPageName && !lastPageName) || (currentPageName && lastPageName)) {
        lastPageName = currentPageName;
        currentPageName = getPathName();
    }
    if (!currentPageName) {
        currentPageName = getPathName();
        return currentPageName;
    }
    return currentPageName ? lastPageName : currentPageName;
};

/**
 * Checks if it's possible to create the data layer or send events
 * @returns {Boolean}
 */
const _canWeCreateDataLayerOrSendEvent = () => {
    return !!window._satellite && !!window.aaHelper;
};

/**
 * Checks the application code and sets the expected application name in tag manager
 * @returns {String} Application Name
 */
const _getApplicationName = () => {
    switch (portalCode) {
        case 'GPA':
            return 'P&C Agent Portal';
        case 'QB':
            return 'P&C Consumer Portal';
        case 'AMP':
            return 'AMP';
        default:
            break;
    }
};

/**
 *
 * @param {String} code
 * @returns {String} Retrieves the name of the product /auto/umbrella/home/
 */
const _getPolicyTypeName = (code) => {
    if(!code){
        return;
    }
    if (MLProductCodeUtil.isPersonalAuto(code)) {
        return "Auto"
    }
    if (MLProductCodeUtil.isPersonalUmbrella(code)) {
        return "Umbrella"
    }
    if (MLProductCodeUtil.isHomeOwnersPartialTypeLandlordHome(code) || MLProductCodeUtil.isHomeOwnersPartialTypeLandlordCondo(code)
        || MLProductCodeUtil.isHomeOwnersPartialTypeHome(code) || MLProductCodeUtil.isHomeOwnersPartialTypeRental(code)
        || MLProductCodeUtil.isHomeOwners(code) || MLProductCodeUtil.isHomeOwnersPartialTypeCondo(code)) {
        return "Home"
    }
};

const _isPolicyChangeFlow = () => {
    return getPathName().indexOf("/endorsement-") > -1;
};

/**
 *  Retrieves product name from state data
 * @returns {String} Product Code
 */
const _getProductCodeFromState = () => {
    if (model) {
        const productCode = _.get(model, 'baseData.productCode') || _.get(model, 'productCode');
        if (productCode) {
            return _getPolicyTypeName(productCode);
        }
    }
    return '';
};

/**
 * Creates or updates the date layer
 */
const _createOrUpdateDigitalData = () => {

    if (!_canWeCreateDataLayerOrSendEvent()) {
        return;
    }

    let oldEvents;
    if (window.digitalData && window.digitalData.event) {
        oldEvents = window.digitalData.event;
    }

    window.digitalData = {
        page: {
            pageInfo: {
                pageName: [window.location.hostname, getPathName()].join('/'),
                pageTitle: getPathName(),
                geoRegion: window.aaHelper.get('region'),
                language: ServiceManager.getService('locale-service').getStoredLanguage(),
                application: _getApplicationName(),
                previousPage: _getLastPageData()
            },
            category: {
                primaryCategory: _getApplicationName(),
                subCategory: _getProductCodeFromState() !== '' ? `P&C_${_getProductCodeFromState()}` : ''
            },
            attributes: {
                country: window.aaHelper.get('country'),
                category: 'Sales',
                audience: window.aaHelper.get('audience'),
                currency: window.aaHelper.get('currency')
            }
        },

        campaign: {
            zipCode: _getUrlParameter('p'),
            gpc: _getUrlParameter('gpc'),
            cid: _getUrlParameter('cid'),
            mc_id: _getUrlParameter('mc_id'),
            oc_id: _getUrlParameter('oc_id'),
            promoid: _getUrlParameter('promoid'),
            clickID: _getUrlParameter('clickID'),
            queryStringParameters: _getQueryStringParameters()
        },

        user: {
            profile: {
                attributes: {
                    customerID: customerID,
                    agentType: agentType
                }
            }
        },

        eventTrack: (cevt, eventDetail) => {
            if (typeof cevt !== 'string' || cevt.length < 1) throw cevt;
            if (typeof window.digitalData.event !== 'object') window.digitalData.event = [];
            if (typeof eventDetail !== 'object') eventDetail = {eventName: cevt};
            window.digitalData.event.push(eventDetail);
            const ev = new CustomEvent(cevt, {'detail': eventDetail});
            document.getElementsByTagName('body')[0].dispatchEvent(ev);
        },

        getEventAttr: () => {
            const ev = window.digitalData.event;
            const i = ev.length - 1;
            return ev[i].attributes;
        }
    };

    if (oldEvents) {
        window.digitalData.event = oldEvents;
    }
};

/**
 *  Retrieves bundle components from state data and bundle service
 * @param {Boolean} force 
 * @returns {String} 
 */
const _getBundleChooseFromState = (force) => {
    if ((model && 
        // model.constructor.name === 'Submission' && 
        model.bundleID_ML === BUNDLED) || force)
    {
        return MLBundleService.getJobs().map((sub) => _getPolicyTypeName(sub.product.publicID)).join('|');
    }
    return '';
};

const _getGroupName = (data) => {
    if (portalCode === 'GPA') {
        return data.baseData.producerType_ML;
    }
    if (data.compRaterVendorName_ML) {
        return data.compRaterVendorName_ML;
    }
    return data.baseData.producerType_ML;
};

/**
 * Triggers an event for state change
 */
const _nexPageEvent = () => {
    const ldo = {
        eventName: 'spaPageView',
        attributes: {
            application: _getApplicationName()
        }
    };

    // CompRater Tagging
    if (model && !_isPolicyChangeFlow() && model.isCompRater && model.isCompRater()) {
        window.digitalData.user.profile.attributes.flowName = 'Comparator';
        window.digitalData.user.profile.attributes.groupName = _getGroupName(model);
    }

    window.digitalData.eventTrack('spaPageView', ldo);
};

/**
 * Updates data layer in state change
 */
const _startStateListener = () => {
    window.addEventListener('locationchange', ()=> {
        _createOrUpdateDigitalData();
        _nexPageEvent();
    })
};

/**
 * Triggers an event for form field error
 * @param {String} fieldName
 */
const _formFieldErrorEvent = (fieldName) => {
    const lastEventElement = window.digitalData.event.length - 1;
    const stateName = getPathName()

    if (window.digitalData.event[lastEventElement].eventName === 'formError' &&
        window.digitalData.event[lastEventElement].attributes.form === stateName)
    {
        if (window.digitalData.event[lastEventElement].attributes.fieldError.indexOf(fieldName) === -1) {
            window.digitalData.event[lastEventElement].attributes.fieldError = window.digitalData.event[lastEventElement].attributes.fieldError.concat(' | ').concat(fieldName);
        }
    } else {
        const ldo = {
            eventName: 'formError',
            attributes: {
                form: stateName,
                fieldError: fieldName
            }
        };

        window.digitalData.eventTrack('formError', ldo);
    }
};

/**
 * Updates data layer on form field error
 */

const _startFormFieldErrorListener = () => {
    //TODO: We don't have this or the ability to add it at input level.
    /*
    $rootScope.$on('formFieldError', (events, args) => {
        _formFieldErrorEvent(args);
    });
    */
};

/**
 * Retrieves a valid formName
 * @returns {String}
 */
const _getFormName = () => {

    let formName = getPathName();

    let stepConfig = mlTagManagerConfig.steps[formName];
    if (stepConfig) {
        formName = stepConfig.form;
    }else{
        const lowerStepName = formName.toLowerCase();
        if(lowerStepName.startsWith("/policies")){
            if(lowerStepName.endsWith("/documents")){
                stepConfig = mlTagManagerConfig.steps['policies.detail.documents'];
            }else if(lowerStepName.endsWith("/summary")){
                stepConfig = mlTagManagerConfig.steps['policies.detail.summary'];
            }else if(lowerStepName.endsWith("/notes")){
                stepConfig = mlTagManagerConfig.steps['policies.detail.notes'];
            }else if(lowerStepName.endsWith("/riskAnalysis")){
                stepConfig = mlTagManagerConfig.steps['policies.detail.rating-factors'];
            }else if(lowerStepName.endsWith("/claims")){
                stepConfig = mlTagManagerConfig.steps['policies.detail.claims'];
            }else if(lowerStepName.endsWith("/billing")){
                stepConfig = mlTagManagerConfig.steps['policies.detail.billing'];
            }
        }if(lowerStepName.startsWith("/accounts")){
            if(lowerStepName.endsWith("/documents")){
                stepConfig = mlTagManagerConfig.steps['accounts.detail.documents'];
            }else if(lowerStepName.endsWith("/summary")){
                stepConfig = mlTagManagerConfig.steps['accounts.detail.summary'];
            }else if(lowerStepName.endsWith("/billingAndPayment")){
                stepConfig = mlTagManagerConfig.steps['accounts.detail.billing'];
            }else if(lowerStepName.endsWith("/claims")){
                stepConfig = mlTagManagerConfig.steps['accounts.detail.claims'];
            }else if(lowerStepName.endsWith("/contacts")){
                stepConfig = mlTagManagerConfig.steps['accounts.detail.contacts'];
            }else if(lowerStepName.endsWith("/summary")){
                stepConfig = mlTagManagerConfig.steps['accounts.detail.summary'];
            }
        }if(lowerStepName.startsWith("/claims/")){
            stepConfig = mlTagManagerConfig.steps['claims.details'];
        }
    }
    if (stepConfig) {
        formName = stepConfig.form;
    }

    return formName;
};

const _getStepName = () => {
    let stepName = getPathName();

    let stepConfig = mlTagManagerConfig.steps[stepName];
    if (stepConfig) {
        stepName = stepConfig.stepName;
    }else{
        const lowerStepName = stepName.toLowerCase();
        if(lowerStepName.startsWith("/policies")){
            if(lowerStepName.endsWith("/documents")){
                stepConfig = mlTagManagerConfig.steps['policies.detail.documents'];
            }else if(lowerStepName.endsWith("/summary")){
                stepConfig = mlTagManagerConfig.steps['policies.detail.summary'];
            }else if(lowerStepName.endsWith("/notes")){
                stepConfig = mlTagManagerConfig.steps['policies.detail.notes'];
            }else if(lowerStepName.endsWith("/riskAnalysis")){
                stepConfig = mlTagManagerConfig.steps['policies.detail.rating-factors'];
            }else if(lowerStepName.endsWith("/claims")){
                stepConfig = mlTagManagerConfig.steps['policies.detail.claims'];
            }else if(lowerStepName.endsWith("/billing")){
                stepConfig = mlTagManagerConfig.steps['policies.detail.billing'];
            }
        }if(lowerStepName.startsWith("/accounts")){
            if(lowerStepName.endsWith("/documents")){
                stepConfig = mlTagManagerConfig.steps['accounts.detail.documents'];
            }else if(lowerStepName.endsWith("/summary")){
                stepConfig = mlTagManagerConfig.steps['accounts.detail.summary'];
            }else if(lowerStepName.endsWith("/billingAndPayment")){
                stepConfig = mlTagManagerConfig.steps['accounts.detail.billing'];
            }else if(lowerStepName.endsWith("/claims")){
                stepConfig = mlTagManagerConfig.steps['accounts.detail.claims'];
            }else if(lowerStepName.endsWith("/contacts")){
                stepConfig = mlTagManagerConfig.steps['accounts.detail.contacts'];
            }else if(lowerStepName.endsWith("/summary")){
                stepConfig = mlTagManagerConfig.steps['accounts.detail.summary'];
            }
        }if(lowerStepName.startsWith("/claims/")){
            stepConfig = mlTagManagerConfig.steps['claims.details'];
        }
    }
    if (stepConfig) {
        stepName = stepConfig.stepName;
    }

    return stepName;
};

/**
 * Sets the product attribute in the payload to be sent with events
 * @param {Object} payload
 */
const _setProductName = (payload) => {
    // Events outside the flow might have product code already
    if (payload.attributes.product) {
        payload.attributes.product = _getPolicyTypeName(payload.attributes.product);
    } else {
        // Get product name from State data
        const product = _getProductCodeFromState();
        if (product !== '') {
            payload.attributes.product = product;
        }
    }
};

/**
 * Sets the bundleChoose attribute in the payload to be sent with events
 * @param {Object} payload
 * @param {Boolean} force
 */
const _setBundleChoose = (payload, force) => {
    if (!payload.attributes.bundleChoose) {
        const bundleChoose = _getBundleChooseFromState(force);
        if (bundleChoose !== '') {
            payload.attributes.bundleChoose = bundleChoose;
        }
    }
};

/**
 * Sets the bundleProduct attribute in the payload to be sent with events
 * @param {Object} payload
 * @param {String} quoteId
 */
const _setBundleProduct = (payload, quoteId) => {
    if (quoteId) {
        const sub = MLBundleService.getJobs().find((job) => job.quoteID === quoteId || job.jobNumber === quoteId);
        payload.attributes.product = _getPolicyTypeName(sub.product.publicID);
    }
};

const _setPolicyChangeFlowAttributes = (payload) => {
    if (!_isPolicyChangeFlow()) {
        return;
    }

    if (payload.attributes.form) {
        payload.attributes.form = `PolicyChange_${payload.attributes.form}`;
    }
    if (payload.attributes.linkName) {
        payload.attributes.linkName = `PolicyChange_${payload.attributes.linkName}`;
    }

    if (model) {
        payload.attributes.policyNumber = _.get(model, 'policyNumber');
    }
};

const _setPrefixApp = (payload) => {
    if (isAgent) {
        if (payload.attributes.form) {
            payload.attributes.form = `P&C_GPA_${payload.attributes.form}`;
        }
        if (payload.attributes.linkName) {
            payload.attributes.linkName = `P&C_GPA_${payload.attributes.linkName}`;
        }
    } else if (portalCode === 'AMP') {
        if (payload.attributes.form) {
            payload.attributes.form = `P&C_AMP_${payload.attributes.form}`;
        }
        if (payload.attributes.linkName) {
            payload.attributes.linkName = `P&C_AMP_${payload.attributes.linkName}`;
        }
    }
};

const _setCompRater = (payload) => {
    if (model/* && _.get(model, 'constructor.name') === 'Submission'*/) {
        if (model.isCompRater && model.isCompRater()) {
            payload.attributes.flowName = 'Comparator';
            payload.attributes.groupName = _.get(model, 'baseData.producerType_ML');
        }
        if (_.get(model, 'compRaterVendorName_ML')) {
            payload.attributes.comparator = _.get(model, 'compRaterVendorName_ML');
        }
    }
};

/**
 * Triggers an event, to be able to trigger the event an @eventName must be passed
 * @param {*} eventName
 * @param {Object} attributes
 */
const _sendEvent = (eventName, attributes) => {
    if (!_canWeCreateDataLayerOrSendEvent()) {
        return;
    }

    // Only send events when there is an event name
    if (eventName === undefined || eventName === '') {
        return;
    }

    // Make sure data layer was created
    if (window.digitalData === undefined) {
        // eslint-disable-next-line no-use-before-define
        _initiateDataLayerAndEvents(_isIE());
    }

    const payload = {
        eventName: eventName,
        attributes: attributes
    };

    payload.attributes.application = _getApplicationName();
    _setProductName(payload);
    _setBundleChoose(payload, false);
    _setPolicyChangeFlowAttributes(payload);
    _setPrefixApp(payload);
    if (portalCode === 'GPA') {
        _setCompRater(payload);
    }

    window.digitalData.eventTrack(eventName, payload);
};

/**
 * Triggers a linkClick event, if no link name is passed the event is not triggered
 * @param {*} linkName
 * @param {String} policyNumber
 * @param {String} searchType
 */
const _sendLinkClickEvent = (linkName, policyNumber, searchType) => {
    if (!linkName || linkName === '') {
        return;
    }

    const attributes = {linkName: linkName};

    if (policyNumber) {
        attributes.policyNumber = policyNumber;
    }

    if (searchType) {
        attributes.searchType = searchType;
    }

    _sendEvent('linkClick', attributes);
};

/**
 * Detects if the browser is IE
 * @returns {Boolean}
 */
const _isIE = () => {
    // eslint-disable-next-line spaced-comment
    return /*@cc_on!@*/false || !!document.documentMode;
};

/**
 * Calls the needed logic to get ready to start events
 * @param {Boolean} isIE
 */
const _initiateDataLayerAndEvents = (isIE) => {
    if (initialized || !_canWeCreateDataLayerOrSendEvent()) {
        return;
    }

    _createOrUpdateDigitalData();
    _startStateListener();
    _startFormFieldErrorListener();
    if (window.lpTag && window.lpTag.events) {
        window.lpTag.events.bind('lpUnifiedWindow', 'state', (eventData) => {
            if (eventData.state === 'initialised') {
                _sendLinkClickEvent('Chat');
            }
        });
    }
    if (!isIE) {
        _nexPageEvent();
    }

    initialized = true;
};

/**
 * Triggers a search event, if no search type is passed the event is not triggered
 * @param {String} searchType
 */
const _sendSearchEvent = (searchType) => {
    _sendEvent('customerSearch', {searchType: searchType});
};

/**
 * Triggers a formStart event
 * @param {String} formName
 */
const _sendFormStartEvent = (formName) => {
    if (!formName || formName === '') {
        return;
    }

    const attributes = {form: formName};

    _sendEvent('formStart', attributes);
};

/**
 * Triggers a formStart event for bundled quote
 * @param {Boolean} edit
 * @param {Object} attributes
 */
const _sendBundleFormStartEvent = (edit, attributes) => {
    let form = 'BundleQuote';
    if (edit && !MLBundleService.inPostQuote()) {
        form = 'EditBundleQuote';
    } else if (!edit && MLBundleService.inPostQuote()) {
        form = 'BundlePostQuote';
    } else if (edit && MLBundleService.inPostQuote()) {
        form = 'EditBundlePostQuote';
    }

    if (!attributes) {
        attributes = {};
    }

    attributes.form = form;
    _setBundleChoose({attributes: attributes}, true);
    _sendEvent('formStart', attributes);
};

/**
 * Triggers a formComplete event
 * @param {String} formName
 * @param {*} formValues
 */
const _sendFormCompleteEvent = (formName, formValues) => {
    if (!formName || formName === '') {
        return;
    }

    const attributes = {form: formName};

    if (formValues) {
        attributes.formValues = formValues;
    }

    _sendEvent('formComplete', attributes);
};

/**
 * Triggers a formComplete event for bundled quote
 */
const _sendBundleFormCompleteEvent = () => {

    const attributes = {form: MLBundleService.inPostQuote() ? 'BundlePostQuote' : 'BundleQuote'};
    _setBundleChoose({attributes: attributes}, true);
    _sendEvent('formComplete', attributes);
};

/**
 * Triggers a formStep event, if no form name is passed the event is not triggered
 * @param {String} formName
 * @param {stepName} stepName
 */
const _sendFormStepEvent = (formName, stepName) => {
    if (!formName || formName === '') {
        return;
    }

    const attributes = {form: formName};

    if (stepName) {
        attributes.stepName = stepName;
    }
    _sendEvent('formStep', attributes);
};

/**
 * Sends quoteComplete event
 * @param {Object} _model
 */
const _sendQuoteCompleteEvent = (_model) => {
    const attr = {
        form: (portalCode === 'GPA') ? 'QuoteForm' : 'YourQuote',
        quoteInputs: {
            state: _model.baseData.policyAddress.state,
            zip: _model.baseData.policyAddress.postalCode
        }
    };

    const chosenQuote = _model.bindData.chosenQuote;
    const offeredQuotes = _model.quoteData.offeredQuotes;
    if (chosenQuote && offeredQuotes) {
        const quote = offeredQuotes.find((q) => q.publicID === chosenQuote);
        if (quote) {
            if (quote.selectedPaymentPlan) {
                attr.quoteInputs.quotePremium = _.get(quote, 'selectedPaymentPlan.total.amount', 0);
            } else if (quote.total) {
                attr.quoteInputs.quotePremium = quote.total.amount;
            }else if (quote.premium.total) {
                attr.quoteInputs.quotePremium = quote.premium.total.amount
            }
        }
    }

    _sendEvent('quoteComplete', attr);
};

/**
 * Handles the events that are triggered between step flows
 * @param {Object} step
 * @param {Object} extraArgs
 */
const _sendEventBetweenSteps = (step, extraArgs) => {
    const stepConfig = mlTagManagerConfig.steps[step];
    if (!stepConfig || (extraArgs && extraArgs.skippedPage)) {
        return;
    }

    // When we come from contact details step, in all flows, leadComplete event has to be triggered too
    if (step.endsWith("/your-info") || step.endsWith("/policy-details")) {
        _sendEvent('leadComplete', {form: stepConfig.form});
    }

    // Send quoteComplete event
    if (step.endsWith("/quote")) {
        _sendQuoteCompleteEvent(model);
        return;
    }

    const attributes = {
        form: stepConfig.form,
        stepName: stepConfig.form === stepConfig.stepName ? stepConfig.stepName : `${stepConfig.form}_${stepConfig.stepName}`,
        product: _.get(model, 'baseData.productCode')
    };

    // Add groupID/GPC
    if (step.endsWith("/affiliation")) {
        const gafInfo = _.get(model, 'baseData.baseData.gafInfo_ML.gpcCode')
        if (gafInfo) {
            attributes.groupId = gafInfo;
        }
    }
    _sendEvent('formStep', attributes);
};

const _sendFormStepPolicyChangeStep = (stepNameParam, formStepParam) => {
    const formName = getPathName()
    const stepConfig = mlTagManagerConfig.steps[formName];
    if (!stepConfig) {
        return;
    }

    const stepName = stepNameParam || stepConfig.stepName;
    const form = formStepParam || stepConfig.form;

    const attributes = {
        form: form,
        stepName: form === stepName ? stepName : `${form}_${stepName}`
    };

    _sendEvent('formStep', attributes);
};

const _sendFormStepCancellationStep = () => {
    const formName = getPathName()
    const stepConfig = mlTagManagerConfig.steps[formName];
    if (!stepConfig) {
        return;
    }

    const attributes = {
        form: `Cancellation_${stepConfig.form}`,
        stepName: stepConfig.form === stepConfig.stepName ? stepConfig.stepName : `${stepConfig.form}_${stepConfig.stepName}`,
        policyNumber: _.get(model, 'policy.policyNumber'),
        product: _.get(model, 'policy.product.publicID')
    };

    _sendEvent('formStep', attributes);
};

/**
 * Handles the events that are triggered between step flows
 */
const _sendFormStepFnolFlow = () => {
    const formName = getPathName()
    const stepConfig = mlTagManagerConfig.steps[formName];
    if (!stepConfig) {
        return;
    }

    const attributes = {
        form: stepConfig.form,
        stepName: stepConfig.form === stepConfig.stepName ? stepConfig.stepName : `${stepConfig.form}_${stepConfig.stepName}`,
    };

    _sendEvent('formStep', attributes);
};

/**
 *  Init method to create data layer and update it when the state changes
 *  The logic inside this init is triggered using window.load to ensure that the adobe tag manager scripts have been loaded
 * @param {Object} _auth
 */
const _init = (_auth) => {
    if(isAgent){
        auth = _auth;
        customerID = _.get(auth, 'authUserData.userName_ML', "");
        agentType = _.get(auth, 'authUserData.userTypeName_ML', "");
        window._agentType = agentType;
    }
    if (_isIE()) {
        $(document).ready(() => {
            _initiateDataLayerAndEvents(true);
        });
    } else {
        window.addEventListener('load', () => {
            _initiateDataLayerAndEvents(false);
        });
    }
};

/**
 * Triggers a formStart event for bundled quote
 * @param {String} eventName
 * @param {Object} attributes
 * @param {String} quoteId
 */
const _sendBundleEvent = (eventName, attributes, quoteId) => {
    if (!attributes) {
        attributes = {};
    }
    _setBundleProduct({attributes: attributes}, quoteId);
    _setBundleChoose({attributes: attributes}, true);
    _sendEvent(eventName, attributes);
};

export default {
    init: _init,
    sendEvent: _sendEvent,
    sendBundleEvent: _sendBundleEvent,
    sendSearchEvent: _sendSearchEvent,
    sendFormCompleteEvent: _sendFormCompleteEvent,
    sendBundleFormCompleteEvent: _sendBundleFormCompleteEvent,
    sendFormStartEvent: _sendFormStartEvent,
    sendBundleFormStartEvent: _sendBundleFormStartEvent,
    sendFormStepEvent: _sendFormStepEvent,
    sendLinkClickEvent: _sendLinkClickEvent,
    sendEventBetweenSteps: _sendEventBetweenSteps,
    sendFormStepPolicyChangeStep: _sendFormStepPolicyChangeStep,
    sendFormStepCancellationStep: _sendFormStepCancellationStep,
    sendFormStepFnolFlow: _sendFormStepFnolFlow,
    getFormName: _getFormName,
    getPolicyTypeName: _getPolicyTypeName,
    getStepName: _getStepName,
    getGroupName: _getGroupName,
    setModel: (_model) => { model = _model }
};
